// "Invoices Included in This View": every invoice the filters match, each row
// opening the invoice read-only. Reporting's Invoices view.
//
// UP TO 1,000 INVOICES, SORTABLE (owner, 2026-11-04 — D-462: "can extend the
// paginating - at least up until like 1,000 records or something? Also,
// clicking the headers in this table should sort the table"). The listing
// reads the first 1,000 matches, 200 at a time (the API's page limit), and
// pages through them on screen 100 at a time. Sorting is over all of them, not
// just the page on screen, which is why they are read up front: the API orders
// by invoice date alone. More than 1,000 matches and the pager says how many
// are not listed; Download still lists up to 5,000.
//
// STATUS IS PLAIN TEXT (owner, 2026-11-04: "Status does not need to be a
// colored pill"), the step's name as the Filter's Status picker reads it.
import { useEffect, useMemo, useState } from 'react'
import {
  Body1,
  Button,
  Caption1,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import type { Session } from '../lib/identity'
import type { Invoice } from '../lib/types'
import { loadDashboardInvoices } from '../lib/data'
import { toInvoiceListParams, type Criteria } from '../lib/criteria'
import { statusLabel } from '../lib/constants'
import { ageInDays, formatCurrency, formatDate, formatTimestampDate } from '../lib/format'
import { sortRows, useTableSort } from '../lib/tableSort'
import { PanelCard } from './DashboardCharts'
import { AgePill } from './Badges'
import { InvoiceDetailModal } from './InvoiceDetailModal'
import { SortableHeaderCell } from './SortableHeaderCell'
import { useClickableRowClass, rowActivationProps } from './tableDensity'

/** The most invoices the listing reads. */
const LISTING_CAP = 1000
/** The API's largest page. */
const READ_SIZE = 200
/** How many invoices the listing shows at a time. */
const PAGE_SIZE = 100

type Column = 'company' | 'vendor' | 'invoiceDate' | 'uploaded' | 'exported' | 'status' | 'total'

/** Each column's sort reader. Dates and money read as text that sorts in order. */
const SORT_KEYS: Record<Column, (inv: Invoice) => string> = {
  company: (inv) => inv.company_name ?? '',
  vendor: (inv) => inv.vendor_name ?? '',
  invoiceDate: (inv) => inv.invoice_date ?? '',
  uploaded: (inv) => inv.uploaded_at ?? '',
  exported: (inv) => inv.exported_at ?? '',
  status: (inv) => statusLabel(inv.status),
  // Fixed width so the collator's digit runs compare as amounts; the offset
  // keeps a credit (a negative total) sorting below zero.
  total: (inv) => ((inv.total ?? 0) + 1e9).toFixed(2).padStart(16, '0'),
}

const useStyles = makeStyles({
  // A floor under the width: below it seven columns wrap their dates and the
  // age pill runs into the next column, so the table scrolls sideways instead.
  scroller: { overflowX: 'auto' },
  listTable: { tableLayout: 'fixed', width: '100%', minWidth: '720px' },
  nowrap: { whiteSpace: 'nowrap' },
  // THE TABLE STAYS MOUNTED WHILE A READ IS IN FLIGHT (owner, 2026-09-15:
  // "everything falls to the bottom before moving up again"). The rows on
  // screen stay until the new ones arrive, and only dim.
  listLoading: { opacity: 0.45, transition: 'opacity 120ms ease-out' },
  right: { textAlign: 'right' },
  rightHeader: { '& .fui-TableHeaderCell__button': { justifyContent: 'flex-end' } },
  ellipsis: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  // The Invoice Date and its days-counting pill on one line (D-451).
  dateWithAge: { display: 'inline-flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' },
  pager: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginTop: '12px',
    color: tokens.colorNeutralForeground3,
  },
  empty: { padding: '48px 0', color: tokens.colorNeutralForeground3 },
})

interface Props {
  session: Session
  criteria: Criteria
  refreshNonce?: number
  /** Which tab's seats grant the source pages: a seat on that tab is the whole
      answer, since every read here runs under that tab's lens (D-258). */
  section: 'dashboard' | 'reporting'
  /** From `App.tsx`: where this invoice is being worked, when the viewer holds
      that step. Drives the modal's "Go To <Step>" button. */
  resolveStep?: (invoice: Invoice) => { label: string; onGo: () => void } | null
  /** Told the server's count of matching invoices each time a read finishes. */
  onTotal?: (total: number | null) => void
}

export function InvoiceListing({ session, criteria, refreshNonce = 0, section, resolveStep, onTotal }: Props) {
  const s = useStyles()
  const clickableRow = useClickableRowClass()
  // A ROW OPENS THE INVOICE, read-only (owner, 2026-09-14). The listing holds
  // every status at once, so there is no single gate the viewer is acting at
  // and no `editStep` to hand the modal.
  const [openInvoice, setOpenInvoice] = useState<Invoice | null>(null)
  // THE SOURCE PAGES, VIEW-ONLY (owner, 2026-10-01).
  const showPdf = session.analyticsCompanies[section].length > 0

  const listParams = useMemo(() => toInvoiceListParams(criteria), [criteria])
  const sort = useTableSort<Column>()

  // `null` rows means "nothing has ever loaded"; `loading` means a read is in
  // flight and the rows on screen are the last ones.
  const [rows, setRows] = useState<Invoice[] | null>(null)
  const [total, setTotal] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [pageIndex, setPageIndex] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    void (async () => {
      const read: Invoice[] = []
      let cursor: string | undefined
      let matched: number | null = null
      try {
        do {
          const page = await loadDashboardInvoices(listParams, cursor, READ_SIZE)
          if (cancelled) return
          read.push(...page.rows)
          matched = page.total ?? matched
          cursor = page.cursor
        } while (cursor !== undefined && read.length < LISTING_CAP)
      } catch {
        if (cancelled) return
        read.length = 0
        matched = null
      }
      setRows(read.slice(0, LISTING_CAP))
      setTotal(matched)
      setPageIndex(0)
      setLoading(false)
      onTotal?.(matched)
    })()
    return () => {
      cancelled = true
    }
  }, [listParams, refreshNonce, onTotal])

  const sorted = useMemo(() => sortRows(rows ?? [], sort.state, SORT_KEYS), [rows, sort.state])
  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE))
  const shown = sorted.slice(pageIndex * PAGE_SIZE, (pageIndex + 1) * PAGE_SIZE)
  const notListed = total !== null && total > sorted.length ? total - sorted.length : 0

  const header = (column: Column, label: string, width: string, right = false) => (
    <SortableHeaderCell
      style={{ width }}
      className={right ? s.rightHeader : undefined}
      sortDirection={sort.directionFor(column)}
      onSort={() => {
        sort.toggle(column)
        setPageIndex(0)
      }}
    >
      {label}
    </SortableHeaderCell>
  )

  return (
    <PanelCard title="Invoices Included in This View">
      {rows !== null && rows.length === 0 && !loading ? (
        <div className={s.empty}>
          <Body1>No invoices match these filters.</Body1>
        </div>
      ) : (
        <>
          <div className={s.scroller}>
          <Table size="small" className={mergeClasses(s.listTable, loading && s.listLoading)} aria-busy={loading}>
            <TableHeader>
              <TableRow>
                {/* Three dates, the days-counting pill right of the Invoice Date (D-451). */}
                {header('company', 'Company', '12%')}
                {header('vendor', 'Vendor', '14%')}
                {header('invoiceDate', 'Invoice Date', '22%')}
                {header('uploaded', 'Uploaded Date', '13%')}
                {header('exported', 'Exported Date', '13%')}
                {header('status', 'Status', '16%')}
                {header('total', 'Total', '10%', true)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((inv) => (
                <TableRow
                  key={inv.id}
                  className={clickableRow}
                  {...rowActivationProps(
                    () => setOpenInvoice(inv),
                    `${inv.vendor_name ?? 'Invoice'} — ${inv.company_name ?? 'no company'}`,
                  )}
                >
                  <TableCell className={s.ellipsis}>{inv.company_name ?? '—'}</TableCell>
                  <TableCell className={s.ellipsis}>{inv.vendor_name ?? '—'}</TableCell>
                  <TableCell>
                    <span className={s.dateWithAge}>
                      {formatDate(inv.invoice_date)}
                      <AgePill days={ageInDays(inv.invoice_date)} />
                    </span>
                  </TableCell>
                  <TableCell className={s.nowrap}>{formatTimestampDate(inv.uploaded_at)}</TableCell>
                  <TableCell className={s.nowrap}>{formatTimestampDate(inv.exported_at)}</TableCell>
                  <TableCell className={s.ellipsis}>{statusLabel(inv.status)}</TableCell>
                  <TableCell className={s.right}>{formatCurrency(inv.total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
          <div className={s.pager}>
            <Button
              size="small"
              disabled={pageIndex === 0 || loading}
              onClick={() => setPageIndex((prev) => Math.max(0, prev - 1))}
            >
              Previous
            </Button>
            <Button
              size="small"
              disabled={pageIndex >= pageCount - 1 || loading}
              onClick={() => setPageIndex((prev) => Math.min(pageCount - 1, prev + 1))}
            >
              Next
            </Button>
            <Caption1>
              {`Page ${pageIndex + 1} of ${pageCount}`}
              {total === null ? '' : ` · ${total} invoice${total === 1 ? '' : 's'}`}
              {notListed > 0 ? ` · ${notListed} past the first ${LISTING_CAP} are not listed` : ''}
            </Caption1>
          </div>
        </>
      )}

      {/* Read-only: no `editStep`, so the modal offers no action bar.
          `screen="archives"` is the same read-only line-item presentation
          Archives asks for — these rows are the same mixed bag of statuses. */}
      <InvoiceDetailModal
        open={!!openInvoice}
        invoice={openInvoice}
        showPdf={showPdf}
        user={session.user}
        viewOnly
        screen="archives"
        // Closes on the way out, so the same invoice is never on screen twice.
        resolveStep={
          resolveStep
            ? (inv) => {
                const target = resolveStep(inv)
                return target
                  ? {
                      label: target.label,
                      onGo: () => {
                        setOpenInvoice(null)
                        target.onGo()
                      },
                    }
                  : null
              }
            : undefined
        }
        onClose={() => setOpenInvoice(null)}
      />
    </PanelCard>
  )
}
